import {
  Alert,
  Box,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  ArrayInput,
  AutocompleteInput,
  Labeled,
  maxValue,
  minValue,
  number,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleFormIterator,
  TextField,
  useGetOne,
  useSimpleFormIteratorItem,
} from "react-admin";
import {
  SUPPLIERS,
  PRODUCTS,
} from "../../../config/resources";
import { formatCurrency } from "../../../utils/currency";
import { integer } from "../../../utils/input-validators";
import { useWatch, useFormContext } from "react-hook-form";
import {
  TPrePurchaseProduct,
  getProductV2,
  TSearchProductV4,
  TProductSupplier,
  TSupplierWarehouse,
} from "@/api";
import { FC, useEffect, useRef } from "react";
import { get } from "lodash";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { purchaseFormAccordion, purchaseFormProductsRefs } from "./atoms";
import { Edit } from "@mui/icons-material";
import useSWR from "swr";
import { useAdmin } from "@/hooks/admin";
import { ProductOptionRendererV4 } from "@/resources/shared/product";

type TPurchaseProductsArrayInput = {
  includeCity?: boolean;
};
export const PurchaseProductsArrayInput = ({
  includeCity,
}: TPurchaseProductsArrayInput) => {
  const products = useWatch({ name: "products" });
  const addressId = useWatch({ name: "addressId" });

  return (
    <>
      {addressId || !includeCity ? (
        <ArrayInput
          source="products"
          defaultValue={[{ id: "", quantity: 1 }]}
          fullWidth
        >
          <SimpleFormIterator
            disableClear
            disableRemove={products?.length <= 1}
            fullWidth
          >
            <PurchaseProductInputs />
          </SimpleFormIterator>
        </ArrayInput>
      ) : (
        <Alert severity="warning">Elige una dirección para ver productos</Alert>
      )}
    </>
  );
};

const PurchaseProductInputs = () => {
  const products = (useWatch({ name: "products" }) as any[]) || [];
  const { index } = useSimpleFormIteratorItem();
  const productInputsRef = useRef<HTMLInputElement>(null);
  const setProductsInputsRef = useSetAtom(purchaseFormProductsRefs);

  useEffect(() => {
    setProductsInputsRef((prev) => ({ ...prev, [index]: productInputsRef }));
  }, [products]);

  return (
    <Box
      ref={productInputsRef}
      sx={{ width: "100%", transition: "background-color 0.5s ease" }}
    >
      <ProductItemSupplierAndProductInputs />
      <ProductItemQuantityInput />
    </Box>
  );
};

const ProductItemSupplierAndProductInputs = () => {
  const { index } = useSimpleFormIteratorItem();
  const supplierId = useWatch({ name: `products.${index}.supplierId` });

  const products = (useWatch({ name: "products" }) as any[]) || [];
  const alreadySelected = products
    .filter((_, i) => i !== index)
    .map((p) => p.id);

  return (
    <Grid container columnSpacing={2} mb={2}>
      <Grid item xs={12} md={6}>
        <ReferenceInput
          reference={SUPPLIERS}
          source={`products.${index}.supplierId`}
        >
          <AutocompleteInput
            label="Filtrar por distribuidor"
            optionText="name"
            optionValue="id"
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
          />
        </ReferenceInput>
      </Grid>
      <Grid item xs={12} md={6}>
        {supplierId ? (
          <ReferenceInput
            reference={PRODUCTS}
            source={`products.${index}.id`}
            filter={{
              supplierId: supplierId,
              isMarketPlace: true,
              status: true,
              type: ["PRODUCT"],
            }}
            sort={{ field: "name", order: "ASC" }}
            perPage={50}
          >
            <AutocompleteInput
              label="Buscar producto"
              filterToQuery={(searchText: string) => ({
                name: searchText,
              })}
              optionText={(choice: TSearchProductV4) => <ProductOptionRendererV4 ignoreStatus product={choice} />}
              inputText={(choice: TSearchProductV4) => choice?.name + " " + choice?.sku}
              validate={[required()]}
              getOptionDisabled={(choice: TSearchProductV4) => alreadySelected.includes(choice?.id)}
              shouldRenderSuggestions={() => true}
              noOptionsText="No hay resultados"
              fullWidth
            />
          </ReferenceInput>
        ) : (
          <AutocompleteInput
            label="Buscar producto"
            source={`products.${index}.id`}
            choices={[]}
            disabled
            noOptionsText="Selecciona un distribuidor primero"
            fullWidth
          />
        )}
      </Grid>
    </Grid>
  );
};


export const ProductItemSupplierInput: FC<{ disabled?: boolean }> = ({
  disabled,
}) => {
  const { trigger } = useFormContext();
  const { index } = useSimpleFormIteratorItem();
  const productId = useWatch({ name: `products.${index}.id` });
  const supplierId = useWatch({ name: `products.${index}.supplierId` });

  const { data: product } = useSWR(
    productId ? { productId } : null,
    ({ productId }) => getProductV2(productId, { marketplace: true })
  );

  useEffect(() => {
    trigger(`products.${index}.quantity`);
  }, [supplierId]);

  return (
    <AutocompleteInput
      source={`products.${index}.supplierId`}
      label="Distribuidor"
      choices={product?.suppliers ?? []}
      inputText={(ps: TProductSupplier) => ps?.supplierName ?? "-"}
      optionText={(ps: TProductSupplier) =>
        ps?.supplierName + " - " + formatCurrency(ps.wholesalePrice)
      }
      optionValue="supplierId"
      emptyText="No hay distribuidores"
      filterToQuery={(searchText: string) => ({
        name: searchText,
      })}
      getOptionDisabled={(ps) => !ps?.status}
      validate={[required()]}
      disabled={disabled || !product}
      fullWidth
    />
  );
};

export const ProductItemQuantityInput: FC<{
  disabled?: boolean;
  current?: number;
}> = ({ disabled, current = 0 }) => {
  const { hasPermission } = useAdmin();
  const { setValue } = useFormContext();
  const { index } = useSimpleFormIteratorItem();
  const productId = useWatch({ name: `products.${index}.id` });
  const supplierId = useWatch({ name: `products.${index}.supplierId` });
  const warehouseId = useWatch({ name: `products.${index}.warehouseId` });

  // Fetch scoped to the selected distributor so products-go returns THAT
  // supplier's warehouses/stock — GET /api/products/v5/:id filters by
  // x-supplier-id (FindByIDAndSupplier), so the BEIO fallback would 404 a
  // product owned by another distributor. The response is flat: the
  // per-warehouse breakdown comes at top-level `availableWarehouses`
  // (`suppliers` is returned empty by that endpoint).
  const { data: product } = useSWR(
    productId && supplierId ? { productId, supplierId } : null,
    ({ productId, supplierId }) =>
      getProductV2(productId, { marketplace: true, supplierId })
  );

  const warehouses = product?.availableWarehouses ?? [];
  const selectedWarehouse = warehouses.find(
    (w) => w.warehouseId === warehouseId
  );

  // Auto-pick a warehouse (prefer one with stock) when none valid is chosen for
  // the current product/supplier. Without a warehouseId the backend silently
  // falls back to the supplier's default warehouse, hiding the choice.
  useEffect(() => {
    if (!warehouses.length) return;
    if (warehouses.some((w) => w.warehouseId === warehouseId)) return;
    const preferred =
      warehouses.find((w) => (w.availableStock ?? 0) > 0) ?? warehouses[0];
    setValue(`products.${index}.warehouseId`, preferred.warehouseId, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [warehouses, warehouseId, index]);

  // Cap by the selected warehouse's stock; fall back to the product aggregate.
  const stockForCap =
    selectedWarehouse?.availableStock ?? product?.availableStock ?? 0;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <SelectInput
          source={`products.${index}.warehouseId`}
          label="Bodega"
          choices={warehouses}
          optionValue="warehouseId"
          optionText={(w: TSupplierWarehouse) =>
            `${w.warehouseName} (${w.availableStock ?? 0} disp.)`
          }
          emptyText="Sin bodegas"
          disabled={disabled || !warehouses.length}
          fullWidth
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <NumberInput
          source={`products.${index}.quantity`}
          min={product?.partialUnitsAllowed ? 0.01 : 1}
          validate={[
            required(),
            number(),
            minValue(product?.partialUnitsAllowed ? 0.1 : 1),
            ...(product?.partialUnitsAllowed ? [] : [integer()]),
            ...(product?.disableInStockOut
              ? [maxValue(current + Math.max(stockForCap, 0))]
              : []),
          ]}
          step={product?.partialUnitsAllowed ? 0.01 : 1}
          defaultValue={1}
          disabled={disabled}
          fullWidth
        />
      </Grid>
      <Grid item xs={6} md={2}>
        <Labeled>
          <TextField
            label="Stock disponible"
            source="availableStock"
            record={
              (selectedWarehouse ?? {
                availableStock: product?.availableStock,
              }) as any
            }
            emptyText="-"
          />
        </Labeled>
      </Grid>
      {hasPermission("purchase.create.mandatoryDiscount") && (
        <>
          <Grid item xs={6} md={2}>
            <NumberInput
              label="Actualizar precio"
              source={`products.${index}.wholesalePrice`}
              helperText="Opcional"
              fullWidth
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <NumberInput
              label="Actualizar precio de compra"
              source={`products.${index}.purchasePrice`}
              helperText="Opcional"
              fullWidth
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export const PurchaseProductBrandArrayInput = ({
  products,
}: {
  products: TPrePurchaseProduct[];
}) => {
  const [expanded, setExpanded] = useAtom(purchaseFormAccordion);
  const { getValues } = useFormContext();
  const productsRefs = useAtomValue(purchaseFormProductsRefs);

  return (
    <List sx={{ padding: 0 }}>
      {products.map((product) => (
        <ListItem key={product.id} sx={{ padding: 0 }}>
          <ListItemText
            primary={
              <>
                {product.name}{" "}
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    const index = getValues().products?.findIndex(
                      (p: any) => p.id === product.id
                    );
                    setExpanded("products");
                    setTimeout(
                      () => {
                        const box =
                          productsRefs[index]?.current?.parentElement
                            ?.parentElement;
                        if (!box) return;
                        box.scrollIntoView({
                          block: "center",
                          behavior: "smooth",
                        });
                        box.style.transition = "background-color 0.5s ease-out";
                        box.style.backgroundColor = "yellow";
                        setTimeout(() => {
                          box.style.backgroundColor = "transparent";
                        }, 500);
                      },
                      expanded !== "products" ? 400 : 0
                    );
                  }}
                >
                  <Edit />
                </IconButton>
              </>
            }
            secondary={
              <span>
                Distribuidor:{" "}
                <ReferenceData
                  resource={SUPPLIERS}
                  id={product.supplierId}
                  source="name"
                />
                <br />
                Precio: {formatCurrency(product.wholesalePrice)}
                <br />
                Cantidad: {product.quantity}
              </span>
            }
          />
          <Box textAlign={"right"}>
            {product.totalDiscount > 0 && (
              <Typography fontSize={"13px"} color="gray">
                <strong>
                  (-
                  {((product.totalDiscount / product.subtotal) * 100).toFixed(
                    2
                  )}
                  %)
                </strong>{" "}
                <span style={{ textDecoration: "line-through" }}>
                  {formatCurrency(product.subtotal)}
                </span>
              </Typography>
            )}
            {formatCurrency(product.subTotalWithDiscount)}
          </Box>
        </ListItem>
      ))}
    </List>
  );
};

const ReferenceData: FC<{ resource: string; id: string; source: string }> = ({
  resource,
  id,
  source,
}) => {
  const { data } = useGetOne(resource, { id });
  return get(data, source) ?? "-";
};
